import currency from "currency.js";
import { PageOrientation, Style, TableCell } from "pdfmake/interfaces";
import { TFunction } from "react-i18next";
import { estimatePdfTextWidth } from "go-list/core/components/Actions/services";
import {
	InvoiceApi,
	InvoiceSummaryApi,
	InvoiceSummaryTaxApi,
	TaxApi,
} from "../../../../../../../../../services/Api/Organization/types";
import { A4WIDTH } from "../../../utils/documentPdf/consts";
import { calculateDocumentColumnWidths, formatDocumentMoney } from "../../../utils/documentPdf/documentTable";
import { INVOICE_CELL_BACKGROUND_COLOR, INVOICE_PDF_TABLE_STYLES, INVOICE_SUMMARY_PDF_COLUMNS } from "./consts";
import { InvoiceSummaryPdfColumn } from "./types";

interface InvoiceSummaryAdditionalRow {
	columns: { columnName: InvoiceSummaryPdfColumn; value: string; noWrap?: boolean }[];
}

export const getInvoiceSummaryTableColumns = () => {
	return [...INVOICE_SUMMARY_PDF_COLUMNS];
};

export const generateInvoiceSummaryTable = (
	t: TFunction,
	data: InvoiceSummaryApi,
	taxes: TaxApi[],
	invoice: InvoiceApi
) => {
	const columns = getInvoiceSummaryTableColumns();
	const widths: (string | number)[] = [];
	const tHead: { style: Style; text: string; border: boolean[] }[] = [];
	const tableBody: TableCell[][] = [];

	columns.forEach((column, index) => {
		tHead.push({
			text: findNameForColumn(column, t),
			style: { ...INVOICE_PDF_TABLE_STYLES.tableHeader, alignment: "right" },
			border: [false, false, false, false],
		});
		widths.push(index === 0 ? "*" : "auto");
	});
	tableBody.push(tHead);

	const table = {
		widths,
		body: tableBody,
		dontBreakRows: true,
		pageOrientation: "portrait" as PageOrientation,
		overWidth: false,
	};
	const newWidthsArray = table.widths.map(() => 0);
	const defaultMaxWidth = ((A4WIDTH - 40) * 1.5) / columns.length;
	const columnWidths: { column: InvoiceSummaryPdfColumn; width: number; minWidth: number }[] = [];
	const invoiceTaxes = [...data.invoice_taxes].sort((a, b) => a.tax.amount - b.tax.amount);

	invoiceTaxes.forEach((item, index) => {
		const bodyRow: { text: { noWrap: boolean; text: string | number }; style: Style; border: boolean[] }[] = [];
		columns.forEach((column, columnIndex) => {
			bodyRow.push({
				text: { noWrap: false, text: getValueForColumn(column, item, taxes) },
				style: {
					...INVOICE_PDF_TABLE_STYLES.tableCell,
					fillColor: index % 2 === 0 ? INVOICE_CELL_BACKGROUND_COLOR : "#fff",
					alignment: columnIndex === 0 ? "left" : "right",
				},
				border: [false, index % 2 === 0, false, index % 2 === 0],
			});

			const row = bodyRow[columnIndex];
			if (row) {
				const headLength = tHead[columnIndex]?.text.length;
				const contentLength = !row.text.text ? headLength : row.text.text.toString().length;
				const text = (headLength > contentLength ? tHead[columnIndex]?.text : row.text.text)?.toString() || "";
				if (text) {
					const width = estimatePdfTextWidth(text, headLength >= contentLength);
					if (width > defaultMaxWidth) {
						newWidthsArray[columnIndex] = defaultMaxWidth;
					} else {
						newWidthsArray[columnIndex] = Math.max(newWidthsArray[columnIndex], width);
					}

					columnWidths.push({
						column,
						minWidth:
							width > defaultMaxWidth ? defaultMaxWidth : Math.max(newWidthsArray[columnIndex], width),
						width,
					});
				}
			}
		});
		table.body.push(bodyRow);
	});

	const restRows = getRestSummaryRows(data, invoice, t);
	const restRowsTableBody: TableCell[][] = [];

	restRows.forEach((item, index) => {
		const finalIndex = invoiceTaxes.length + index;
		const bodyRow: {
			text: { noWrap: boolean; text: string | number } | {};
			style: Style;
			border: boolean[];
			colSpan?: number;
		}[] = [];

		const reversedItemColumns = [...item.columns].reverse();
		const reversedColumns = [...columns].reverse();

		reversedItemColumns.forEach((column, columnIndex) => {
			const nextItemColumn = reversedItemColumns[columnIndex + 1];
			const startIndex = reversedColumns.indexOf(column.columnName);
			const endIndex = reversedColumns.indexOf(nextItemColumn?.columnName);
			const colSpan = endIndex - startIndex;

			bodyRow.push({
				text: { noWrap: column.noWrap ?? false, text: column.value, colSpan: colSpan === 0 ? 1 : colSpan },
				style: {
					...INVOICE_PDF_TABLE_STYLES.tableCell,
					fillColor: finalIndex % 2 === 0 ? INVOICE_CELL_BACKGROUND_COLOR : "#fff",
					alignment: columnIndex === reversedItemColumns.length - 1 ? "left" : "right",
				},
				border: [false, finalIndex % 2 === 0, false, finalIndex % 2 === 0],
			});
			if (colSpan > 1) {
				for (let i = 0; i < colSpan - 1; i++) {
					bodyRow.push({
						style: {
							...INVOICE_PDF_TABLE_STYLES.tableCell,
							fillColor: finalIndex % 2 === 0 ? INVOICE_CELL_BACKGROUND_COLOR : "#fff",
						},
						border: [false, finalIndex % 2 === 0, false, finalIndex % 2 === 0],
						text: {},
					});
				}
			}
		});
		restRowsTableBody.push(bodyRow);
	});

	restRowsTableBody.forEach((tableRow) => {
		tableRow.reverse();
		table.body.push(tableRow);
	});

	table.widths = newWidthsArray.map((width, index) => {
		return width || table.widths[index];
	});

	const tableWidthSum: number = table.widths
		.map((width) => {
			return Number(width) || 0;
		})
		.reduce((acc, curr) => {
			return acc + curr;
		}, 0);

	const CELL_PADDING = 7;
	const fullTableWidth = tableWidthSum + CELL_PADDING * columns.length;

	if (fullTableWidth > A4WIDTH - 40) {
		table.widths = table.widths.map(() => {
			return "auto";
		});
	} else {
		table.widths = calculateDocumentColumnWidths(table.widths, columnWidths, []);
	}

	return table;
};

const getValueForColumn = (column: InvoiceSummaryPdfColumn, summary: InvoiceSummaryTaxApi, taxes: TaxApi[]) => {
	switch (column) {
		case InvoiceSummaryPdfColumn.TOTAL_PRICE_GROSS: {
			return formatDocumentMoney(summary.price_gross);
		}
		case InvoiceSummaryPdfColumn.TOTAL_PRICE_NET: {
			return formatDocumentMoney(summary.price_net);
		}
		case InvoiceSummaryPdfColumn.TAX: {
			const taxName = taxes.find(({ id }) => Number(id) === Number(summary.tax.id))?.name;
			return taxName ?? "";
		}
		case InvoiceSummaryPdfColumn.EMPTY: {
			return "";
		}
		case InvoiceSummaryPdfColumn.TAX_PRICE: {
			return formatDocumentMoney(summary.price_tax);
		}
	}
};

const findNameForColumn = (column: InvoiceSummaryPdfColumn, t: TFunction) => {
	return getColumnsWithName(t).find(({ id }) => id === column)?.name ?? "";
};

const getColumnsWithName = (t: TFunction) => {
	return [
		{
			id: InvoiceSummaryPdfColumn.TOTAL_PRICE_GROSS,
			name: t("modules.invoice.field.total_price_gross.title"),
		},
		{
			id: InvoiceSummaryPdfColumn.TOTAL_PRICE_NET,
			name: t("modules.invoice.field.total_price_net.title"),
		},
		{
			id: InvoiceSummaryPdfColumn.TAX,
			name: `${t("common.word.vat_rate")} ${t("common.word.vat")}`,
		},
		{
			id: InvoiceSummaryPdfColumn.TAX_PRICE,
			name: `${t("common.word.tax")} ${t("common.word.vat")}`,
		},
		{
			id: InvoiceSummaryPdfColumn.EMPTY,
			name: "",
		},
	];
};

const getRestSummaryRows = (
	summary: InvoiceSummaryApi,
	invoice: InvoiceApi,
	t: TFunction
): InvoiceSummaryAdditionalRow[] => {
	return [
		{
			columns: [
				{
					columnName: InvoiceSummaryPdfColumn.EMPTY,
					value: t("modules.invoice.field.all_together.title"),
					noWrap: true,
				},
				{
					columnName: InvoiceSummaryPdfColumn.TAX,
					value: "",
				},
				{
					columnName: InvoiceSummaryPdfColumn.TOTAL_PRICE_NET,
					value: formatDocumentMoney(summary.price_sum_net),
				},
				{
					columnName: InvoiceSummaryPdfColumn.TAX_PRICE,
					value: formatDocumentMoney({
						amount: summary.invoice_taxes.reduce(
							(acc, curr) => currency(acc).add(curr.price_tax.amount).value,
							0
						),
						currency: summary.price_sum_net.currency,
					}),
				},
				{
					columnName: InvoiceSummaryPdfColumn.TOTAL_PRICE_GROSS,
					value: formatDocumentMoney(summary.price_sum_gross),
				},
			],
		},
		{
			columns: [
				{
					columnName: InvoiceSummaryPdfColumn.EMPTY,
					value: t("modules.invoice.field.price_paid.title"),
					noWrap: true,
				},
				{
					columnName: InvoiceSummaryPdfColumn.TOTAL_PRICE_GROSS,
					value: formatDocumentMoney(summary.price_paid),
					noWrap: true,
				},
			],
		},
		{
			columns: [
				{
					columnName: InvoiceSummaryPdfColumn.EMPTY,
					value: t("modules.invoice.field.amount_to_pay.title"),
					noWrap: true,
				},
				{
					columnName: InvoiceSummaryPdfColumn.TOTAL_PRICE_GROSS,
					value: formatDocumentMoney(summary.price_to_pay),
					noWrap: true,
				},
			],
		},
		{
			columns: [
				{
					columnName: InvoiceSummaryPdfColumn.EMPTY,
					value: t("modules.invoice.field.bank_account.title"),
					noWrap: true,
				},
				{
					columnName: InvoiceSummaryPdfColumn.TOTAL_PRICE_GROSS,
					value: invoice.issuer?.bank_account
						? `${invoice.issuer.bank_account.name} ${invoice.issuer.bank_account.number}`
						: "-",
					noWrap: true,
				},
			],
		},
		{
			columns: [
				{
					columnName: InvoiceSummaryPdfColumn.EMPTY,
					value: t("common.word.comment.title"),
					noWrap: true,
				},
				{
					columnName: InvoiceSummaryPdfColumn.TOTAL_PRICE_GROSS,
					value: invoice.notes || "-",
					noWrap: true,
				},
			],
		},
	];
};
